import { createContext, createRef } from 'preact';
import { useContext, useEffect } from 'preact/hooks';
import { PreviewContext } from './creative/contexts/previewContext';
import { useAdParameters } from './creative/hooks/useAdParameters';
import { useAdSize } from './creative/hooks/useAdSize';
import { sumo, SumoLevel } from './sumo';
import { getLastVideoMetadata } from './teads-api';

export function detectAbnormalAspectRatio(creativeId: string, width: number, height: number) {
  const videoMetadata = getLastVideoMetadata();

  if (!videoMetadata) return;

  const requestedRatio = videoMetadata.width / videoMetadata.height;
  const receivedRatio = width / height;

  const diff = Math.abs(requestedRatio - receivedRatio);

  if (diff > 0.01) {
    sumo(
      {
        ci: creativeId,
        e: `ABNORMAL_RATIO`,
        em: [
          `requested_width=${videoMetadata.width}`,
          `requested_height=${videoMetadata.height}`,
          `received_width=${width}`,
          `received_height=${height}`,
          `requested_ratio=${requestedRatio}`,
          `received_ratio=${receivedRatio}`,
          `builder=${process.env.BUILDER_NAME}`,
        ].join(' '),
      },
      SumoLevel.ERROR
    );
  }
}

function detectBlackBarSize(
  creativeId: string,
  elementWidth: number,
  elementHeight: number,
  assetWidth: number,
  assetHeight: number,
  adWidth: number,
  adHeight: number
) {
  if (!elementWidth || !elementHeight || !assetWidth || !assetHeight) {
    return;
  }

  const assetRatio = assetWidth / assetHeight;

  const horizontalBlackBarsSize = Math.max(0, (elementWidth - elementHeight * assetRatio) / 2);
  const verticalBlackBarsSize = Math.max(0, (elementHeight - elementWidth / assetRatio) / 2);

  if (horizontalBlackBarsSize < 5 && verticalBlackBarsSize < 5) {
    return;
  }

  sumo(
    {
      ci: creativeId,
      e: `BLACK_BARS_SIZE`,
      em: [
        `builder=${process.env.BUILDER_NAME}`,
        `horizontalSize=${Math.round(horizontalBlackBarsSize)}`,
        `verticalSize=${Math.round(verticalBlackBarsSize)}`,
        `elementWidth=${Math.round(elementWidth)}`,
        `elementHeight=${Math.round(elementHeight)}`,
        `assetWidth=${Math.round(assetWidth)}`,
        `assetHeight=${Math.round(assetHeight)}`,
        `adWidth=${Math.round(adWidth)}`,
        `adHeight=${Math.round(adHeight)}`,
      ].join(' '),
    },
    SumoLevel.INFO
  );
}

const TimeoutIdContext = createContext(createRef<ReturnType<typeof setTimeout>>());

export function useBlackBarSizeDetector(
  elementWidth: number,
  elementHeight: number,
  assetWidth: number,
  assetHeight: number
) {
  const adSize = useAdSize();
  const { creativeId } = useAdParameters();
  const timeoutIdRef = useContext(TimeoutIdContext);
  const isPreview = useContext(PreviewContext);

  useEffect(() => {
    if (isPreview) {
      return;
    }

    timeoutIdRef.current && clearTimeout(timeoutIdRef.current);
    timeoutIdRef.current = setTimeout(() => {
      detectBlackBarSize(creativeId, elementWidth, elementHeight, assetWidth, assetHeight, adSize.width, adSize.height);
    }, 250);
    return () => timeoutIdRef.current && clearTimeout(timeoutIdRef.current);
  }, [isPreview, creativeId, elementWidth, elementHeight, assetWidth, assetHeight, adSize.width, adSize.height]);
}
